import { useCallback, useMemo } from "react";
import Swal from "sweetalert2";
import { v4 as uuidv4 } from "uuid";
import { useBudget } from "../../../../contexts/BudgetContext/projectbudgets.context";
import { useProject } from "../../../../contexts/project.context";
import { createEmptyVersionTotals } from "../../../../contexts/BudgetContext/calc-methods";
import {
  Budget,
  BudgetSummaryActionOptions,
  CalcOption,
  initialBudgetWideAppliedTaxes,
} from "../../../../types/budget/budget";
import { serviceValues } from "../../../../types/budget/service-item";
import { TabActive } from "../../../../types/project.type";
import { createOptionsFromEnum } from "../../../../utils/formatters";
import { isSupplierVisibleInVersion } from "../../../../utils/budget-stage-visibility";
import { toast } from "react-toastify";

/**
 * Número da negociação como o usuário a vê. O índice de versão conta a versão de
 * assessoria, que não entra na numeração exibida — mesma regra do rótulo da barra
 * inferior (`hasConsultingBefore` em `ActiveBudget`). Como `newVersion` é sempre a
 * última, qualquer versão de assessoria existente já vem antes dela.
 */
export function getNegotiationDisplayNumber(
  versionHistory: { isConsulting?: boolean }[] | undefined,
  newVersion: number
): number {
  const hasConsulting = versionHistory?.some((el) => el.isConsulting) ?? false;
  return hasConsulting ? newVersion - 1 : newVersion;
}

type UseBudgetSummaryActionsParams = {
  budget: Budget;
  version: number;
  hasConsultingVersion?: boolean;
  isLastNegotiation?: boolean;
};

export function useBudgetSummaryActions({
  budget,
  version,
  hasConsultingVersion = false,
  isLastNegotiation = true,
}: UseBudgetSummaryActionsParams) {
  const { createBudget, updateBudget, isBudgetDisabled } = useBudget();
  const { tabActive } = useProject();

  const actionOptions = createOptionsFromEnum(BudgetSummaryActionOptions);

  type ActionKey = keyof typeof BudgetSummaryActionOptions;

  const actionMethods = useMemo(
    () => ({
      APROVE: () => {
        if (!budget.type) {
          Swal.fire({
            title: "Tipo de orçamento não selecionado",
            text: "É necessário selecionar o tipo de orçamento (Logística ou Produção) antes de enviar para fechamento.",
            icon: "warning",
            confirmButtonColor: "#3085d6",
            confirmButtonText: "Ok",
          });
          return;
        }

        Swal.fire({
          title: "Alterar status do orçamento?",
          text: `Você está alterando o status do ${
            budget.name || `Orçamento ${budget.budgetIndex + 1}`
          } para Aprovado.`,
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          cancelButtonText: "Cancelar",
          confirmButtonText: "Sim, alterar",
          reverseButtons: true,
        }).then((result) => {
          if (result.isConfirmed && !!budget._id) {
            const newVersionIndex = budget.versionHistory.length;
            const baseValuesFromSourceVersion = budget.baseValues.filter(
              (el) => el.version === version
            );

            const sourceTotals =
              version === 0
                ? budget.budgetValuesTotal
                : budget.negociationValuesTotal[version - 1] ||
                  createEmptyVersionTotals();

            const closingTotalsFromSourceVersion = sourceTotals
              ? JSON.parse(JSON.stringify(sourceTotals))
              : createEmptyVersionTotals();

            const currentBudgetWideAppliedTaxes =
              budget.budgetWideAppliedTaxes.find(
                (el) => el.version === version
              );
            const extrasValue =
              currentBudgetWideAppliedTaxes?.taxData?.extras?.value ?? 0;
            let extrasAutoZeroed: {
              originalValue: number;
              originalTypeTax: string;
            } | null = null;

            if (extrasValue > 0) {
              extrasAutoZeroed = {
                originalValue:
                  currentBudgetWideAppliedTaxes!.taxData.extras.value,
                originalTypeTax:
                  currentBudgetWideAppliedTaxes!.taxData.extras.typeTax,
              };
              if (closingTotalsFromSourceVersion) {
                closingTotalsFromSourceVersion.extraTotals = {
                  totalInBRL: 0,
                  totalInCurrency: 0,
                };
              }
              if (closingTotalsFromSourceVersion?.byCalculationType?.SUM?.SUM) {
                closingTotalsFromSourceVersion.byCalculationType.SUM.SUM.extraTotals =
                  {
                    totalInBRL: 0,
                    totalInCurrency: 0,
                  };
              }
            }

            const cpbudgetServices = budget.budgetServices.map((service) => {
              const isInSourceVersion =
                version === 0 || (service.versions ?? []).includes(version);
              const sourceValues = isInSourceVersion
                ? version === 0
                  ? service.budgetValues
                  : service.negotiationValue[version - 1] || serviceValues
                : serviceValues;

              const copiedValues = JSON.parse(JSON.stringify(sourceValues));

              return {
                ...service,
                closingValues: isInSourceVersion
                  ? copiedValues
                  : service.closingValues,
                versions: isInSourceVersion
                  ? [...service.versions, newVersionIndex]
                  : service.versions,
              };
            });

            updateBudget(
              budget._id,
              {
                ...budget,
                budgetServices: cpbudgetServices,
                closingValuesTotal: closingTotalsFromSourceVersion,
                versionHistory: [
                  ...budget.versionHistory,
                  {
                    status: "APPROVED",
                    version: newVersionIndex,
                    comparisonVersion: version,
                  },
                ],
                baseValues: [
                  ...budget.baseValues,
                  ...baseValuesFromSourceVersion.map((el) => {
                    return { ...el, version: newVersionIndex };
                  }),
                ],
                mainAppliedTaxes: [
                  ...budget.mainAppliedTaxes,
                  ...budget.mainAppliedTaxes
                    .filter((el) => el.version === version)
                    .map((el) => ({ ...el, version: newVersionIndex })),
                ],
                budgetWideAppliedTaxes: [
                  ...budget.budgetWideAppliedTaxes,
                  {
                    ...initialBudgetWideAppliedTaxes,
                    version: newVersionIndex,
                    exchange:
                      currentBudgetWideAppliedTaxes?.exchange ||
                      initialBudgetWideAppliedTaxes.exchange,
                  },
                ],
                // #MMV-2672: leva o adiantamento (Resumo de Nota) da versão de
                // origem para a nova versão -- mesmo valor, até o usuário mudar.
                noteAdvances: [
                  ...(budget.noteAdvances ?? []),
                  ...(budget.noteAdvances ?? [])
                    .filter((el) => el.version === version)
                    .map((el) => ({ ...el, version: newVersionIndex })),
                ],
                ...(extrasAutoZeroed ? { extrasAutoZeroed } : {}),
              },
              "APPROVED",
              newVersionIndex
            );

            Swal.fire({
              title: "Atualizar",
              text: "Status atualizado",
              icon: "success",
            });
          }
        });
      },
      DENY: () => {
        const isClosingTab = tabActive === TabActive.APPROVED;
        const actionType = isClosingTab ? "DENIED_CLOSING" : "DENIED";
        const newStatus = isClosingTab ? "DENIED_CLOSING" : "DENIED";

        Swal.fire({
          title: isClosingTab ? "Reprovar fechamento?" : "Reprovar negociação?",
          text: isClosingTab
            ? `Você está reprovando o fechamento de ${
                budget.name || `Orçamento ${budget.budgetIndex + 1}`
              }. O registro será mantido para histórico.`
            : `Você está reprovando a negociação de ${
                budget.name || `Orçamento ${budget.budgetIndex + 1}`
              }. O registro será mantido para histórico.`,
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          cancelButtonText: "Cancelar",
          confirmButtonText: "Sim, reprovar",
          reverseButtons: true,
        }).then((result) => {
          if (result.isConfirmed && !!budget._id) {
            updateBudget(
              budget._id,
              {
                ...budget,
                versionHistory: budget.versionHistory.map((el) => {
                  if (el.version === version) {
                    el.status = newStatus;
                  }
                  return el;
                }),
              },
              actionType,
              version
            );
          }
        });
      },
      NEGOTIATE: () => {
        if (!budget.type) {
          Swal.fire({
            title: "Tipo de orçamento não selecionado",
            text: "É necessário selecionar o tipo de orçamento (Logística ou Produção) antes de enviar para negociação.",
            icon: "warning",
            confirmButtonColor: "#3085d6",
            confirmButtonText: "Ok",
          });
          return;
        }

        Swal.fire({
          title: "Alterar status do orçamento?",
          text: `Você está alterando o status de ${
            budget.name || `Orçamento ${budget.budgetIndex + 1}`
          } para Negociação.`,
          icon: "info",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          cancelButtonText: "Cancelar",
          confirmButtonText: "Sim, alterar",
          reverseButtons: true,
        }).then((result) => {
          if (result.isConfirmed && !!budget._id) {
            const cpbudgetServices = budget.budgetServices.map((service) => {
              const sourceValues =
                version === 0
                  ? service.budgetValues
                  : service.negotiationValue[version - 1] || serviceValues;

              const copiedValues = JSON.parse(JSON.stringify(sourceValues));
              const newVersionIndex = budget.versionHistory.length;

              const updatedNegotiationValue =
                version === 0 &&
                service.negotiationValue.length === 1 &&
                newVersionIndex === 1
                  ? [copiedValues]
                  : [...service.negotiationValue, copiedValues];

              return {
                ...service,
                negotiationValue: updatedNegotiationValue,
                versions: [...service.versions, newVersionIndex],
              };
            });
            const negotiationTotalsFromSourceVersion = budget.budgetValuesTotal;

            updateBudget(
              budget._id,
              {
                ...budget,
                negotiationVersion: budget.negotiationVersion + 1,
                versionHistory: [
                  ...budget.versionHistory,
                  {
                    status: "NEGOTIATE",
                    version: budget.versionHistory.length,
                    comparisonVersion: version,
                  },
                ],
                budgetServices: cpbudgetServices,
                negociationValuesTotal: [
                  ...budget.negociationValuesTotal,
                  negotiationTotalsFromSourceVersion ||
                    createEmptyVersionTotals(),
                ],
                baseValues: [
                  ...budget.baseValues,
                  ...budget.baseValues.map((el) => {
                    return { ...el, version: budget.versionHistory.length };
                  }),
                ],
                mainAppliedTaxes: [
                  ...budget.mainAppliedTaxes,
                  ...budget.mainAppliedTaxes
                    .filter((el) => el.version === version)
                    .map((el) => ({
                      ...el,
                      version: budget.versionHistory.length,
                    })),
                ],
                budgetWideAppliedTaxes: [
                  ...budget.budgetWideAppliedTaxes,
                  ...budget.budgetWideAppliedTaxes
                    .filter((el) => el.version === version)
                    .map((el) => ({
                      ...el,
                      version: budget.versionHistory.length,
                    })),
                ],
                // #MMV-2672: leva o adiantamento (Resumo de Nota) da versão de
                // origem para a nova versão -- mesmo valor, até o usuário mudar.
                noteAdvances: [
                  ...(budget.noteAdvances ?? []),
                  ...(budget.noteAdvances ?? [])
                    .filter((el) => el.version === version)
                    .map((el) => ({
                      ...el,
                      version: budget.versionHistory.length,
                    })),
                ],
              },
              "NEGOTIATE",
              budget.versionHistory.length
            );

            Swal.fire({
              title: "Atualizar",
              text: "Status atualizado",
              icon: "success",
            });
          }
        });
      },
      DUPLICATE: () => {
        if (!budget.type) {
          Swal.fire({
            title: "Tipo de orçamento não selecionado",
            text: "É necessário selecionar o tipo do orçamento antes de duplicar o orçamento",
            icon: "warning",
            confirmButtonColor: "#3085d6",
            confirmButtonText: "Ok",
          });
          return;
        }

        Swal.fire({
          title: "Duplicar orçamento?",
          text: `Você está duplicando ${
            budget.name || `Orçamento ${budget.budgetIndex + 1}`
          }.`,
          icon: "info",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          cancelButtonText: "Cancelar",
          confirmButtonText: "Sim, duplicar",
          reverseButtons: true,
        }).then(async (result) => {
          if (result.isConfirmed && !!budget._id) {
            const newBudget = JSON.parse(JSON.stringify(budget));
            delete newBudget._id;
            newBudget.negotiationVersion = 0;
            newBudget.status = "OPEN";

            // A cópia reproduz o ORÇAMENTO ORIGINAL (v0), não o estado final.
            // Sem o filtro, serviços criados dentro da negociação/fechamento
            // (cujo `versions` não contém 0) eram promovidos ao orçamento base da
            // cópia com valor zerado — as "linhas fantasmas" do export.
            newBudget.budgetServices = newBudget.budgetServices
              .filter((service: any) => (service.versions ?? []).includes(0))
              .map((service: any) => {
                const {
                  _id,
                  versions,
                  negotiationValue,
                  closingValues,
                  ...cleanService
                } = service;
                return {
                  ...cleanService,
                  _id: uuidv4(),
                  versions: [0],
                  negotiationValue: [],
                  closingValues: undefined,
                };
              });

            // Defensivo: orçamentos gravados antes desta correção podem ter
            // `supplierList`/`addedItemTypeList` já podados pelo bug (entradas
            // faltando) ou sobrando para serviços que não vieram na cópia. Recria a
            // lista a partir dos serviços mantidos, preservando as entradas que
            // ainda não têm serviço algum (fornecedor/categoria vazios).
            const keptSupplierKeys: string[] = Array.from(
              new Set<string>(
                newBudget.budgetServices.map(
                  (s: any) => `${s.supplierId}|${s.calcOption}`
                )
              )
            );
            const keptCategoryKeys: string[] = Array.from(
              new Set<string>(
                newBudget.budgetServices.map(
                  (s: any) => `${s.supplierId}|${s.type}|${s.calcOption}`
                )
              )
            );
            const originalSupplierKeys = new Set<string>(
              (budget.budgetServices ?? []).map(
                (s: any) => `${s.supplierId}|${s.calcOption}`
              )
            );
            const originalCategoryKeys = new Set<string>(
              (budget.budgetServices ?? []).map(
                (s: any) => `${s.supplierId}|${s.type}|${s.calcOption}`
              )
            );

            newBudget.supplierList = newBudget.supplierList.filter(
              (el: any) => {
                const key = `${el.supplierId}|${el.calcOption}`;
                // mantém se tem serviço na cópia, ou se nunca teve serviço nenhum
                return (
                  keptSupplierKeys.includes(key) ||
                  !originalSupplierKeys.has(key)
                );
              }
            );
            newBudget.addedItemTypeList = newBudget.addedItemTypeList.filter(
              (el: any) => {
                const key = `${el.supplierId}|${el.type}|${el.calcOption}`;
                return (
                  keptCategoryKeys.includes(key) ||
                  !originalCategoryKeys.has(key)
                );
              }
            );
            // Recria as entradas que o bug havia removido, para a cópia não nascer
            // com serviço órfão (sem card de fornecedor/categoria para desenhá-lo).
            for (const key of keptSupplierKeys) {
              const [supplierId, calcOption] = key.split("|");
              const exists = newBudget.supplierList.some(
                (el: any) =>
                  el.supplierId === supplierId && el.calcOption === calcOption
              );
              if (!exists)
                newBudget.supplierList.push({ supplierId, calcOption });
            }
            for (const key of keptCategoryKeys) {
              const [supplierId, type, calcOption] = key.split("|");
              const exists = newBudget.addedItemTypeList.some(
                (el: any) =>
                  el.supplierId === supplierId &&
                  el.type === type &&
                  el.calcOption === calcOption
              );
              if (!exists)
                newBudget.addedItemTypeList.push({
                  supplierId,
                  type,
                  calcOption,
                });
            }

            newBudget.negociationValuesTotal = [];
            newBudget.closingValuesTotal = undefined;
            newBudget.baseValues = newBudget.baseValues
              .filter((el: any) => el.version === 0)
              .map((el: any) => ({ ...el, version: 0 }));
            newBudget.mainAppliedTaxes = newBudget.mainAppliedTaxes
              .filter((el: any) => el.version === 0)
              .map((el: any) => ({ ...el, version: 0 }));
            newBudget.budgetWideAppliedTaxes = newBudget.budgetWideAppliedTaxes
              .filter((el: any) => el.version === 0)
              .map((el: any) => ({ ...el, version: 0 }));

            await createBudget({
              ...newBudget,
              name: `${budget.name} (Cópia)`,
              versionHistory: [
                {
                  status: "OPEN",
                  version: 0,
                  comparisonVersion: 0,
                },
              ],
            });

            Swal.fire({
              title: "Atualizar",
              text: "Orçamento duplicado",
              icon: "success",
            });
          }
        });
      },
      DELETE: () => {
        Swal.fire({
          title: "Tem certeza que deseja excluir este orçamento?",
          text: `Você está excluindo permanentemente ${
            budget.name || `Orçamento ${budget.budgetIndex + 1}`
          }. Esta ação não pode ser desfeita`,
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          cancelButtonText: "Cancelar",
          confirmButtonText: "Sim, excluir permanentemente",
          reverseButtons: true,
        }).then((result) => {
          if (result.isConfirmed && !!budget._id) {
            updateBudget(
              budget._id,
              {
                ...budget,
                negotiationVersion:
                  budget.negotiationVersion > 1
                    ? budget.negotiationVersion - 1
                    : 0,
                versionHistory: budget.versionHistory.map((el) => {
                  if (el.version === version) {
                    el.status = "DELETED";
                  }
                  return el;
                }),
              },
              "DELETED",
              version
            );
            Swal.fire({
              title: "Atualizar",
              text: "Orçamento apagado",
              icon: "success",
            });
          }
        });
      },
      NEW_VERSION: async () => {
        if (!budget.type) {
          await Swal.fire({
            title: "Tipo de orçamento não selecionado",
            text: "É necessário selecionar o tipo de orçamento (Logística ou Produção) antes de criar uma nova versão de negociação.",
            icon: "warning",
            confirmButtonColor: "#3085d6",
            confirmButtonText: "Ok",
          });
          return;
        }

        await Swal.fire({
          title: "Criar nova versão?",
          text: `Você está criando uma nova versão de negociação para ${
            budget.name || `Orçamento ${budget.budgetIndex + 1}`
          }.`,
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          cancelButtonText: "Cancelar",
          confirmButtonText: "Sim, criar",
          reverseButtons: true,
        }).then(async (result) => {
          if (result.isConfirmed && !!budget._id) {
            const baseValuesFromSourceVersion = budget.baseValues.filter(
              (el) => el.version === version
            );
            const negotiationTotalsFromSourceVersion =
              budget.negociationValuesTotal.find(
                (_el, elIndex) => elIndex === version - 1
              );
            const updated = await updateBudget(
              budget._id,
              {
                ...budget,
                budgetServices: budget.budgetServices.map((service) => {
                  const isInSourceVersion = (service.versions ?? []).includes(
                    version
                  );
                  // Sempre empurra um slot em negotiationValue para manter os índices alinhados.
                  // Só adiciona ao service.versions se o serviço estava na versão fonte.
                  const sourceValues = isInSourceVersion
                    ? service.negotiationValue[version - 1] || serviceValues
                    : serviceValues;
                  const copiedValues = JSON.parse(JSON.stringify(sourceValues));
                  return {
                    ...service,
                    versions: isInSourceVersion
                      ? [...service.versions, budget.versionHistory.length]
                      : service.versions,
                    negotiationValue: [
                      ...service.negotiationValue,
                      copiedValues,
                    ],
                  };
                }),
                baseValues: [
                  ...budget.baseValues,
                  ...baseValuesFromSourceVersion.map((el) => {
                    return { ...el, version: budget.versionHistory.length };
                  }),
                ],
                negotiationVersion: budget.negotiationVersion + 1,
                negociationValuesTotal: [
                  ...budget.negociationValuesTotal,
                  negotiationTotalsFromSourceVersion ||
                    createEmptyVersionTotals(),
                ],
                versionHistory: [
                  ...budget.versionHistory,
                  {
                    status: "NEGOTIATE",
                    version: budget.versionHistory.length,
                    comparisonVersion: version,
                  },
                ],
                mainAppliedTaxes: [
                  ...budget.mainAppliedTaxes,
                  ...budget.mainAppliedTaxes
                    .filter((el) => el.version === version)
                    .map((el) => ({
                      ...el,
                      version: budget.versionHistory.length,
                    })),
                ],
                budgetWideAppliedTaxes: [
                  ...budget.budgetWideAppliedTaxes,
                  ...budget.budgetWideAppliedTaxes
                    .filter((el) => el.version === version)
                    .map((el) => ({
                      ...el,
                      version: budget.versionHistory.length,
                    })),
                ],
                // #MMV-2672: leva o adiantamento (Resumo de Nota) da versão de
                // origem para a nova versão -- mesmo valor, até o usuário mudar.
                noteAdvances: [
                  ...(budget.noteAdvances ?? []),
                  ...(budget.noteAdvances ?? [])
                    .filter((el) => el.version === version)
                    .map((el) => ({
                      ...el,
                      version: budget.versionHistory.length,
                    })),
                ],
              },
              "NEW_VERSION",
              budget.versionHistory.length
            );

            // `updateBudget` devolve null quando a API falha (ele mesmo já
            // notifica o erro) — só confirma sucesso quando gravou. O teste é
            // por null, não por valor verdadeiro: o PUT pode responder corpo
            // vazio ("", como o próprio ramo SAVE do contexto já trata) e isso
            // é sucesso, não erro.
            if (updated != null) {
              toast.success(
                `Negociação ${getNegotiationDisplayNumber(
                  budget.versionHistory,
                  budget.versionHistory.length
                )} criada com sucesso`
              );
            }
          }
        });
      },
      RENAME: async () => {
        await Swal.fire({
          title: "Editar Nome",
          input: "text",
          inputValue: budget.name,
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          cancelButtonText: "Cancelar",
          confirmButtonText: "Sim, alterar",
          reverseButtons: true,
          inputValidator: (value) => {
            if (!value) return "O nome não pode estar vazio!";
          },
        }).then(async (result) => {
          if (result.isConfirmed && budget._id) {
            await updateBudget(
              budget._id,
              { ...budget, name: result.value },
              "RENAME",
              version
            );
            Swal.fire({
              title: "Atualizar",
              text: "O status do orçamento foi atualizado",
              icon: "success",
            });
          }
        });
      },
      CONSULTING: async () => {
        if (!budget.type) {
          await Swal.fire({
            title: "Tipo de orçamento não selecionado",
            text: "É necessário selecionar o tipo de orçamento (Logística ou Produção) antes de criar uma versão de assessoria.",
            icon: "warning",
            confirmButtonColor: "#3085d6",
            confirmButtonText: "Ok",
          });
          return;
        }

        await Swal.fire({
          title: "Criar versão assessoria",
          text: `Você está criando uma nova versão de assessoria para ${
            budget.name || `Orçamento ${budget.budgetIndex + 1}`
          }.`,
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          cancelButtonText: "Cancelar",
          confirmButtonText: "Sim, criar",
          reverseButtons: true,
        }).then((result) => {
          if (result.isConfirmed && !!budget._id) {
            const baseValuesFromSourceVersion = budget.baseValues.filter(
              (el) => el.version === version
            );
            const negotiationTotalsFromSourceVersion = budget.budgetValuesTotal;
            updateBudget(
              budget._id,
              {
                ...budget,
                budgetServices: budget.budgetServices.map((service) => {
                  const isInSourceVersion = (service.versions ?? []).includes(
                    version
                  );
                  const sourceValues = isInSourceVersion
                    ? service.negotiationValue[version - 1] || serviceValues
                    : serviceValues;
                  const copiedValues = JSON.parse(JSON.stringify(sourceValues));
                  return {
                    ...service,
                    versions: isInSourceVersion
                      ? [...service.versions, budget.versionHistory.length]
                      : service.versions,
                    negotiationValue: [
                      ...service.negotiationValue,
                      copiedValues,
                    ],
                  };
                }),
                baseValues: [
                  ...budget.baseValues,
                  ...baseValuesFromSourceVersion.map((el) => {
                    return { ...el, version: budget.versionHistory.length };
                  }),
                ],
                negotiationVersion: budget.negotiationVersion + 1,
                negociationValuesTotal: [
                  ...budget.negociationValuesTotal,
                  negotiationTotalsFromSourceVersion ||
                    createEmptyVersionTotals(),
                ],
                versionHistory: [
                  ...budget.versionHistory,
                  {
                    status: "NEGOTIATE",
                    version: budget.versionHistory.length,
                    comparisonVersion: 0,
                    isConsulting: true,
                  },
                ],
                mainAppliedTaxes: [
                  ...budget.mainAppliedTaxes,
                  ...budget.mainAppliedTaxes
                    .filter((el) => el.version === version)
                    .map((el) => ({
                      ...el,
                      version: budget.versionHistory.length,
                    })),
                ],
                budgetWideAppliedTaxes: [
                  ...budget.budgetWideAppliedTaxes,
                  ...budget.budgetWideAppliedTaxes
                    .filter((el) => el.version === version)
                    .map((el) => ({
                      ...el,
                      version: budget.versionHistory.length,
                    })),
                ],
                // #MMV-2672: leva o adiantamento (Resumo de Nota) da versão de
                // origem para a nova versão -- mesmo valor, até o usuário mudar.
                noteAdvances: [
                  ...(budget.noteAdvances ?? []),
                  ...(budget.noteAdvances ?? [])
                    .filter((el) => el.version === version)
                    .map((el) => ({
                      ...el,
                      version: budget.versionHistory.length,
                    })),
                ],
              },
              "CONSULTING",
              budget.versionHistory.length
            );
            Swal.fire({
              title: "Atualizar",
              text: "Nova versão criada",
              icon: "success",
            });
          }
        });
      },
    }),
    [budget, version, tabActive, createBudget, updateBudget]
  );

  const triggerAction = useCallback(
    (action: ActionKey) => actionMethods[action](),
    [actionMethods]
  );

  const filteredActions = useMemo(() => {
    if (isBudgetDisabled) {
      switch (tabActive) {
        case TabActive.BUDGET:
          return actionOptions?.filter(
            (i: { label: string }) =>
              i?.label === BudgetSummaryActionOptions.DUPLICATE
          );
        case TabActive.NEGOTIATION:
          return [];
        default:
          return actionOptions;
      }
    }

    let filtered = actionOptions;

    if (hasConsultingVersion || tabActive === TabActive.BUDGET) {
      filtered = filtered?.filter(
        (i: { label: string }) =>
          i?.label !== BudgetSummaryActionOptions.CONSULTING
      );
    }

    if (tabActive === TabActive.NEGOTIATION && !isLastNegotiation) {
      filtered = filtered?.filter(
        (i: { label: string }) =>
          i?.label !== BudgetSummaryActionOptions.NEW_VERSION
      );
    }

    return filtered;
  }, [
    actionOptions,
    tabActive,
    isBudgetDisabled,
    hasConsultingVersion,
    isLastNegotiation,
  ]);

  const secondaryActions = useMemo(() => {
    const primaryKeysByTab: Record<string, string[]> = {
      [TabActive.BUDGET]: ["NEGOTIATE", "APROVE"],
      [TabActive.NEGOTIATION]: ["APROVE"],
      [TabActive.APPROVED]: ["DENY"],
    };
    const primaryKeys = primaryKeysByTab[tabActive] ?? ["NEGOTIATE", "APROVE"];

    return filteredActions.filter((i: { value: string }) => {
      if (primaryKeys.includes(i.value)) return false;

      // "Criar nova versão" virou o botão "Nova negociação" na barra inferior;
      // manter no menu duplicaria a ação.
      if (i.value === "NEW_VERSION") return false;

      // Excluir fica fora do menu "Mais opções".
      if (i.value === "DELETE") return false;

      if (tabActive === TabActive.APPROVED && i.value !== "DENY") {
        return false;
      }
      if (
        tabActive === TabActive.NEGOTIATION &&
        ["DUPLICATE", "NEGOTIATE", "DELETE", "RENAME"].includes(i.value)
      ) {
        return false;
      }
      if (tabActive === TabActive.BUDGET && i.value === "NEW_VERSION") {
        return false;
      }

      return true;
    });
  }, [filteredActions, tabActive]);

  return {
    triggerAction,
    filteredActions,
    secondaryActions,
    actionMethods,
  };
}

export function useBudgetSave() {
  const {
    activeBudget,
    activeCalcOption,
    categoriesSnapshot,
    updateBudget,
    isBudgetDisabled,
    versionInfo,
  } = useBudget();

  const handleSave = useCallback(async () => {
    if (isBudgetDisabled) return;

    if (!activeBudget.type) {
      Swal.fire({
        title: "Tipo de orçamento não selecionado",
        text: "É necessário selecionar o tipo do orçamento",
        icon: "warning",
        confirmButtonColor: "#3085d6",
        confirmButtonText: "Ok",
      });
      return;
    }

    // `supplierList` não é versionada: um fornecedor removido numa etapa
    // anterior (Negociação) continua na lista, apenas oculto na tela atual
    // (ver `BudgetSupplier/index.tsx`). Sem filtrar por visibilidade aqui,
    // essa entrada oculta é contada como duplicata e bloqueia o Salvar mesmo
    // não havendo nenhuma duplicidade visível para o usuário corrigir.
    const suppliersInCalc = activeBudget?.supplierList?.filter(
      (el) =>
        el.calcOption === activeCalcOption &&
        isSupplierVisibleInVersion(
          activeBudget?.budgetServices,
          el.supplierId,
          activeCalcOption,
          versionInfo?.version ?? 0,
          el.deletedInVersions,
          versionInfo?.status
        )
    );
    const nonTemporaryIds = suppliersInCalc
      ?.filter((el) => !el.supplierId.includes("-"))
      ?.map((el) => el.supplierId);
    const hasDuplicateSupplier = nonTemporaryIds?.some(
      (id, idx) => !!id && nonTemporaryIds.indexOf(id) !== idx
    );
    if (hasDuplicateSupplier) {
      toast.error(
        "Existem fornecedores duplicados na mesma forma de pagamento. Corrija antes de salvar."
      );
      return;
    }

    // Em Totais os switches S/N ficam desabilitados, então não há como
    // definir categorias ali.
    const isTotalsView = activeCalcOption === "budgetTotals";
    const hasUndefinedCategories =
      !isTotalsView &&
      categoriesSnapshot?.some((el) => {
        const budgetActive: any = activeBudget;
        const value =
          budgetActive?.itemTypeList?.[activeCalcOption as CalcOption]?.[
            el.value
          ];
        return value === undefined || value === null;
      });

    if (hasUndefinedCategories) {
      toast.error(
        "Existem categorias sem definição (S/N). Na lista de Categorias, marque S ou N para cada categoria."
      );
      return;
    }

    if (activeBudget._id) {
      try {
        await updateBudget(activeBudget._id, activeBudget, "SAVE", -1);
        toast.success("Alterações salvas.");
      } catch {
        toast.error("Erro ao salvar alterações.");
      }
    }
  }, [
    activeBudget,
    activeCalcOption,
    categoriesSnapshot,
    updateBudget,
    isBudgetDisabled,
    versionInfo,
  ]);

  return { handleSave, isBudgetDisabled };
}
